import React from 'react';

// Heat colours from cold (untouched) to hot (heavily trained). The bands follow the weekly
// set ranges in Schoenfeld 2017 and Pelland 2026: <5 low, 5–9 moderate, 10–20 growth, 20+.
export const ZONE_COLORS = {
  None: 'var(--st-heat-none, #d5dae1)',
  Low: '#fde68a',
  Moderate: '#fdba74',
  Optimal: '#f97316',
  High: '#b91c1c'
};
export const ZONES = ['None', 'Low', 'Moderate', 'Optimal', 'High'];

// Simple anatomical shapes on a 120 × 250 canvas per figure. Paired muscles are mirrored.
const FRONT = [
  { muscle: 'Shoulders', shapes: [['ellipse', 35, 54, 10, 8], ['ellipse', 85, 54, 10, 8]] },
  { muscle: 'Chest', shapes: [['ellipse', 50, 66, 11, 8.5], ['ellipse', 70, 66, 11, 8.5]] },
  { muscle: 'Biceps', shapes: [['ellipse', 29, 82, 6, 13], ['ellipse', 91, 82, 6, 13]] },
  { muscle: 'Forearms', shapes: [['ellipse', 25, 110, 5, 14], ['ellipse', 95, 110, 5, 14]] },
  { muscle: 'Abs', shapes: [['rect', 51, 77, 18, 38, 5]] },
  { muscle: 'Obliques', shapes: [['rect', 42, 80, 8, 30, 4], ['rect', 70, 80, 8, 30, 4]] },
  { muscle: 'Quads', shapes: [['ellipse', 50, 158, 9.5, 26], ['ellipse', 70, 158, 9.5, 26]] },
  { muscle: 'Calves', shapes: [['ellipse', 50, 210, 6, 17], ['ellipse', 70, 210, 6, 17]] }
];

const BACK = [
  { muscle: 'UpperBack', shapes: [['ellipse', 60, 60, 20, 10]] },
  { muscle: 'RearDelts', shapes: [['ellipse', 35, 54, 9, 7.5], ['ellipse', 85, 54, 9, 7.5]] },
  { muscle: 'Lats', shapes: [['ellipse', 47, 86, 9, 18], ['ellipse', 73, 86, 9, 18]] },
  { muscle: 'Triceps', shapes: [['ellipse', 29, 82, 6, 13], ['ellipse', 91, 82, 6, 13]] },
  { muscle: 'Forearms', shapes: [['ellipse', 25, 110, 5, 14], ['ellipse', 95, 110, 5, 14]] },
  { muscle: 'LowerBack', shapes: [['rect', 52, 100, 16, 16, 4]] },
  { muscle: 'Glutes', shapes: [['ellipse', 51, 128, 10, 10], ['ellipse', 69, 128, 10, 10]] },
  { muscle: 'Hamstrings', shapes: [['ellipse', 50, 162, 9, 23], ['ellipse', 70, 162, 9, 23]] },
  { muscle: 'Calves', shapes: [['ellipse', 50, 210, 7, 17], ['ellipse', 70, 210, 7, 17]] }
];

function Silhouette() {
  // Head, torso, arms and legs in a neutral tone behind the muscles.
  return (
    <g className="st-body-base">
      <circle cx="60" cy="20" r="12" />
      <rect x="54" y="30" width="12" height="10" rx="3" />
      <path d="M38 44 Q60 38 82 44 L86 64 L80 118 Q60 126 40 118 L34 64 Z" />
      <path d="M33 46 L22 70 L18 124 L26 126 L34 84 L40 60 Z" />
      <path d="M87 46 L98 70 L102 124 L94 126 L86 84 L80 60 Z" />
      <path d="M40 116 L38 186 L42 236 L56 236 L58 186 L60 128 Z" />
      <path d="M80 116 L82 186 L78 236 L64 236 L62 186 L60 128 Z" />
    </g>
  );
}

function Figure({ title, regions, byMuscle, t }) {
  return (
    <figure className="st-body-figure">
      <svg viewBox="0 0 120 250" role="img" aria-label={title}>
        <Silhouette />
        {regions.map(({ muscle, shapes }) => {
          const load = byMuscle[muscle];
          const zone = load?.zone || 'None';
          const label = `${load?.name || muscle}: ${t('strength.heat.setsPerWeek', { count: Number(load?.weeklySets || 0) })}`;
          return (
            <g key={muscle} className={`st-muscle st-zone-${zone}`} fill={ZONE_COLORS[zone]} data-muscle={muscle} role="img" aria-label={label}>
              <title>{label}</title>
              {shapes.map(([kind, ...a], i) => (kind === 'rect'
                ? <rect key={i} x={a[0]} y={a[1]} width={a[2]} height={a[3]} rx={a[4]} />
                : <ellipse key={i} cx={a[0]} cy={a[1]} rx={a[2]} ry={a[3]} />))}
            </g>
          );
        })}
      </svg>
      <figcaption>{title}</figcaption>
    </figure>
  );
}

/** Front and back body drawings coloured by weekly working sets per muscle. */
function BodyHeatMap({ t, muscles, compact = false }) {
  const byMuscle = Object.fromEntries((muscles || []).map(m => [m.muscle, m]));
  return (
    <div className={`st-heatmap ${compact ? 'compact' : ''}`}>
      <div className="st-body-figures">
        <Figure title={t('strength.heat.front')} regions={FRONT} byMuscle={byMuscle} t={t} />
        <Figure title={t('strength.heat.back')} regions={BACK} byMuscle={byMuscle} t={t} />
      </div>
      <ul className="st-heat-legend" aria-label={t('strength.heat.legend')}>
        {ZONES.map(zone => (
          <li key={zone}>
            <span className="st-heat-swatch" style={{ background: ZONE_COLORS[zone] }} aria-hidden="true" />
            {t(`strength.heat.zones.${zone}`)}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default BodyHeatMap;
